<template>
  <div class="ref-nums-section-new">
    <div v-if="showInputs" class="formgrid grid">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="new-reference-number-edit-type"
        :errors="v$.typeId.$errors"
        :label="
          t('common.paramTypes', {
            param: t('referenceData.referenceNumbers'),
          })
        "
        class="field col-6"
      >
        <RygenDropdown
          :id="inputId"
          v-model="newReferenceNumber.type"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="typeOptions"
          data-key="id"
          option-label="name"
          filter
          :placeholder="
            t('common.paramTypes', {
              param: t('referenceData.referenceNumbers'),
            })
          "
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="new-reference-number-edit-value"
        :errors="v$.value.$errors"
        :label="
          newReferenceNumber?.typeName ||
          t('common.paramValues', {
            param: t('referenceData.referenceNumbers'),
          })
        "
        :help-text="realReferenceNumberType?.description"
        class="field col-6"
      >
        <RygenDropdown
          v-if="
            clientReferenceNumberType?.referenceNumberType?.valueType ===
            ReferenceNumberValueType.DROPDOWN
          "
          :id="inputId"
          v-model="newReferenceNumber.value"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :editable="
            clientReferenceNumberType.referenceNumberType.allowFreeformValue
          "
          :options="clientReferenceNumberType.referenceNumberType.valueOptions"
          option-label="label"
          option-value="value"
          filter
          :placeholder="
            newReferenceNumber?.typeName ||
            t('common.paramValues', {
              param: t('referenceData.referenceNumbers'),
            })
          "
        />
        <RygenCalendar
          v-else-if="isDateOrTimeType"
          :id="inputId"
          v-model="newReferenceNumber.valueAsDateTime"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :show-time="isTimeType"
          :time-only="
            clientReferenceNumberType?.referenceNumberType?.valueType ===
            ReferenceNumberValueType.TIME
          "
          hour-format="12"
          :placeholder="
            newReferenceNumber?.typeName ||
            t('common.paramValues', {
              param: t('referenceData.referenceNumbers'),
            })
          "
        />
        <InputNumber
          v-else-if="
            clientReferenceNumberType?.referenceNumberType?.valueType ===
            ReferenceNumberValueType.NUMERIC
          "
          v-model.trim="newReferenceNumber.valueAsNumber"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :input-id="inputId"
          :placeholder="
            newReferenceNumber?.typeName ||
            t('common.paramValues', {
              param: t('referenceData.referenceNumbers'),
            })
          "
          @keypress.enter.prevent="onSubmit"
        />
        <span
          v-else-if="isFreeformMultiChoice"
          class="flex flex-gap-1 flex-grow-1"
        >
          <RygenMultiSelect
            v-bind="ariaProps"
            :id="inputId"
            v-model="newReferenceNumber.valueAsArray"
            :class="inputClasses"
            :invalid="invalid"
            :options="
              clientReferenceNumberType?.referenceNumberType?.valueOptions
            "
            option-label="label"
            option-value="value"
            filter
            :placeholder="
              newReferenceNumber?.typeName ||
              t('common.paramValues', {
                param: t('referenceData.referenceNumbers'),
              })
            "
          />
          <InputText
            :id="inputId"
            v-model.trim="newReferenceNumber.value"
            :placeholder="
              newReferenceNumber?.typeName ||
              t('common.paramValues', {
                param: t('referenceData.referenceNumbers'),
              })
            "
            @keypress.enter.prevent="onSubmit"
          />
        </span>
        <RygenMultiSelect
          v-else-if="isMultiChoice"
          v-bind="ariaProps"
          :id="inputId"
          v-model="newReferenceNumber.valueAsArray"
          :class="inputClasses"
          :invalid="invalid"
          :options="
            clientReferenceNumberType?.referenceNumberType?.valueOptions
          "
          option-label="label"
          option-value="value"
          filter
          :placeholder="
            newReferenceNumber?.typeName ||
            t('common.paramValues', {
              param: t('referenceData.referenceNumbers'),
            })
          "
        />
        <InputText
          v-else
          :id="inputId"
          v-model.trim="newReferenceNumber.value"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :placeholder="
            newReferenceNumber?.typeName ||
            t('common.paramValues', {
              param: t('referenceData.referenceNumbers'),
            })
          "
          @keypress.enter.prevent="onSubmit"
        />
      </InputWrapper>
    </div>
    <div class="ref-nums-section-new-buttons flex justify-content-end">
      <div v-if="showInputs">
        <RygenButton
          mode="tertiary"
          :label="t('common.remove')"
          icon="pi pi-times"
          icon-pos="left"
          @click="onRemove"
        />
        <RygenButton
          mode="tertiary"
          :label="t('common.done')"
          :disabled="v$.$invalid"
          icon="pi pi-check"
          icon-pos="left"
          @click="onSubmit"
        />
      </div>
      <AddButton
        v-else-if="editable"
        class="p-button-sm align-self-end"
        :label="t('referenceData.referenceNumbers')"
        @click="onAdd"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import { computed, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import {
  ClientReferenceNumberType,
  ReferenceNumberType,
  ReferenceNumberTypeLevel,
  ReferenceNumberValueType,
} from '@rygen/rygen-ui/model';

import { useReferenceNumberValueTypes } from '@/composables';
import { ReferenceNumber, ReferenceNumberSource } from '@/model';

import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

const props = defineProps({
  resourceId: {
    type: Number,
    required: true,
  },
  clientTypes: {
    type: Array as PropType<ClientReferenceNumberType[]>,
    default: () => [],
  },
  typeOptions: {
    type: Array as PropType<ReferenceNumberType[]>,
    default: () => [],
  },
  level: {
    type: String as PropType<ReferenceNumberTypeLevel>,
    default: '',
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { resourceId, clientTypes, typeOptions, level, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'reference-number-create', newRefNum: ReferenceNumber): void;
}>();

const { t } = useI18n();

const newReferenceNumber: Ref<ReferenceNumber> = ref(new ReferenceNumber());
const showInputs = ref(false);

const rules = computed(() => {
  const r: Record<string, Record<string, ValidationRule>> = {
    typeId: {},
    value: {},
  };

  if (showInputs.value) {
    r.typeId = {
      required: required(
        t('common.paramTypes', {
          param: t('referenceData.referenceNumbers'),
        }),
      ),
    };
    r.value = {
      required: required(
        t('common.paramValues', {
          param: t('referenceData.referenceNumbers'),
        }),
      ),
    };
  }

  return r;
});

const { v$, validate } = useValidations(newReferenceNumber, rules);

const clientReferenceNumberType = computed(() => {
  return clientTypes.value.find(
    (clientType) =>
      clientType.referenceNumberType?.naturalId &&
      clientType.referenceNumberType.naturalId ===
        newReferenceNumber.value?.type?.naturalId,
  );
});

const realReferenceNumberType = computed(
  () => clientReferenceNumberType.value?.referenceNumberType,
);

const { isDateOrTimeType, isMultiChoice, isFreeformMultiChoice, isTimeType } =
  useReferenceNumberValueTypes(clientReferenceNumberType);

const onAdd = () => {
  newReferenceNumber.value = new ReferenceNumber();
  showInputs.value = true;
};

const onRemove = () => {
  showInputs.value = false;
};

const onSubmit = async () => {
  if (!(await validate())) {
    return;
  }
  newReferenceNumber.value.source = new ReferenceNumberSource({
    type: level.value,
    resourceId: resourceId.value,
  });
  newReferenceNumber.value.typeNaturalId = typeOptions.value.find(
    (type) => type.name === newReferenceNumber.value?.typeName,
  )?.naturalId;
  newReferenceNumber.value.value = newReferenceNumber.value?.value?.replace(
    /[^\0-\x7F]/g,
    '',
  );
  emit('reference-number-create', newReferenceNumber.value);

  showInputs.value = false;
};

watch(
  realReferenceNumberType,
  (
    newVal: ReferenceNumberType | undefined,
    oldVal: ReferenceNumberType | undefined,
  ) => {
    const oldValIsDropdown =
      oldVal?.valueType === ReferenceNumberValueType.DROPDOWN ||
      oldVal?.valueType === ReferenceNumberValueType.MULTI_CHOICE;
    if (
      newVal?.valueType &&
      (newVal.valueType !== oldVal?.valueType || oldValIsDropdown)
    ) {
      newReferenceNumber.value.value = undefined;
      v$.value.$reset();
    }
  },
);
watch(showInputs, (newVal: boolean, oldVal: boolean) => {
  if (newVal && !oldVal) {
    v$.value.$reset();
  }
});
</script>

<style>
.ref-nums-section-new {
  .input-wrapper {
    label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
